import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Icon} from '@iconify/react';
import {createPortal} from 'react-dom';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import AnswerCommentSheet from '../../../../../ui/components/common/AnswerCommentSheet';

interface AnswerCommentProps {
    comment: string;
    showComment: boolean;
    setShowComment?: (show: boolean) => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
    device?: DeviceType;
    answer?: string;
}

function AnswerComment({comment, showComment, setShowComment, anchorRef, device, answer}: AnswerCommentProps): ReactElement {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [dropdownPlacement, setDropdownPlacement] = useState<'top' | 'bottom'>('bottom');
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(() => (typeof document !== 'undefined' ? document.body : null));
    const [hoverOpened, setHoverOpened] = useState(false);
    const isMobile = device === DeviceType.MOBILE;

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const localSlideWorkspace = anchorRef.current?.closest('#canvas-holder');
        if (localSlideWorkspace instanceof HTMLElement) {
            setPortalRoot(localSlideWorkspace);
        } else {
            setPortalRoot(document.body);
        }
    }, [anchorRef]);

    useEffect(() => {
        if (isMobile || !showComment || !anchorRef.current || !portalRoot) {
            return (): void => undefined;
        }

        const updateDropdownPosition = (): void => {
            const rect = anchorRef.current?.getBoundingClientRect();
            const slideRect = portalRoot.getBoundingClientRect();
            if (!rect || !slideRect) return;

            const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
            const spaceAbove = rect.top - slideRect.top;
            const spaceBelow = slideRect.bottom - rect.bottom;
            const shouldOpenTop = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
            const maxHeight = Math.max((shouldOpenTop ? spaceAbove : spaceBelow) - 8, 80);

            setDropdownPlacement(shouldOpenTop ? 'top' : 'bottom');

            setDropdownStyle({
                position: 'absolute',
                top: shouldOpenTop ? 'auto' : rect.bottom - slideRect.top - 3,
                bottom: shouldOpenTop ? slideRect.bottom - rect.top - 3 : 'auto',
                left: rect.left - slideRect.left,
                width: rect.width,
                maxHeight,
            });
        };

        updateDropdownPosition();
        requestAnimationFrame(updateDropdownPosition);

        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return (): void => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isMobile, showComment, portalRoot, anchorRef]);

    useEffect(() => {
        if (isMobile || !showComment) {
            return (): void => undefined;
        }

        const handleClickOutside = (event: MouseEvent): void => {
            const path = event.composedPath();
            const isInsideAnchor = !!anchorRef.current && path.includes(anchorRef.current);
            const isInsideDropdown = !!dropdownRef.current && path.includes(dropdownRef.current);

            if (!isInsideAnchor && !isInsideDropdown) {
                setShowComment?.(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, showComment, anchorRef, setShowComment]);

    return isMobile ? (
        <>
            <div
                className="c-questionElement__answer_comment"
                onClick={(e) => {
                    e.stopPropagation();
                    setShowComment?.(true);
                }}
            >
                <Icon icon="solar:chat-line-bold" />
            </div>
            <AnswerCommentSheet open={showComment} onDismiss={() => setShowComment?.(false)} answer={answer ?? ''} comment={comment} />
        </>
    ) : (
        <>
            <>
                <div
                    className="c-questionElement__answer_comment"
                    onClick={() => {
                        setShowComment?.(!showComment);
                        setHoverOpened(false);
                    }}
                    onMouseEnter={() => {
                        if (!showComment) {
                            setShowComment?.(true);
                            setHoverOpened(true);
                        }
                    }}
                    onMouseLeave={() => {
                        if (hoverOpened) {
                            setShowComment?.(false);
                            setHoverOpened(false);
                        }
                    }}
                >
                    <Icon icon="solar:chat-line-bold" />
                </div>
                {portalRoot &&
                    createPortal(
                        <div
                            ref={dropdownRef}
                            className={`c-questionElement__answer_dropdown ${showComment ? 'is-visible' : ''} ${
                                dropdownPlacement === 'top' ? 'is-top' : ''
                            }`}
                            style={dropdownStyle}
                        >
                            {comment}
                        </div>,
                        portalRoot
                    )}
            </>
        </>
    );
}

export default AnswerComment;
